import React, { useState } from "react";
import { Check, Upload, FileText, Link as LinkIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STEPS = [
  { key: "proposal", label: "Proposal", url_field: "proposal_url", helper: "Upload your event proposal document.", sample: "https://docs.google.com/document/d/sample" },
  { key: "budget", label: "Curation of Budget", url_field: "budget_url", helper: "Curate the event budget. Use the Budget tab for the calculator." },
  { key: "secretariat_form", label: "Secretariat Forms", url_field: "secretariat_form_url", helper: "Paste the secretariat form link.", sample: "https://forms.gle/sample-sec" },
  { key: "registration_school", label: "Registration Form (School)", url_field: "registration_school_url", helper: "School registration link.", sample: "https://forms.gle/sample-school" },
  { key: "registration_individual", label: "Registration Form (Individual)", url_field: "registration_individual_url", helper: "Individual registration link.", sample: "https://forms.gle/sample-ind" },
  { key: "committees_agendas", label: "Committees & Agendas", helper: "Configure committees in the Committees tab." },
  { key: "vendors", label: "Vendor Allocation", helper: "Allocate vendors from the Vendors tab." },
  { key: "trophies", label: "Trophies Data", helper: "Add trophies & awards in Vendors tab." },
  { key: "collaborations", label: "Collaborations", helper: "Add collaborations from Vendors tab." },
  { key: "invitation_brochure", label: "Invitation Brochure", url_field: "invitation_brochure_url", helper: "Brochure link / URL." },
  { key: "sponsorship_brochure", label: "Sponsorship Brochure", url_field: "sponsorship_brochure_url", helper: "Sponsorship brochure link." },
  { key: "social_media_calendar", label: "Social Media Calendar", helper: "Assigned to your Social Media Manager." },
  { key: "cash_prizes", label: "Cash Prizes", helper: "Track cash prizes in Vendors > Trophies." },
  { key: "attendance_sheet", label: "Attendance Sheet", helper: "Auto-generated from Allotment tab." },
  { key: "marking_sheet", label: "Marking Sheet", helper: "Auto-generated from Allotment tab." },
  { key: "art_craft", label: "Art & Craft", helper: "Plan art & craft activities." },
  { key: "merchandise_goodies", label: "Merchandise & Goodies", helper: "Manage in Vendors tab." },
];

export default function WorkflowTab({ event, reload }) {
  const [editing, setEditing] = useState(null);
  const [tmpUrl, setTmpUrl] = useState("");
  const [adminSamples, setAdminSamples] = useState({});
  const ws = event.workflow_status || {};

  React.useEffect(() => {
    api.get("/samples").then((r) => setAdminSamples(r.data || {})).catch(() => {});
  }, []);

  const toggle = async (step, value) => {
    await api.post(`/events/${event.event_id}/workflow/${step}?value=${value}`);
    reload();
  };

  const saveUrl = async (step) => {
    if (!tmpUrl) return;
    const upd = { [step.url_field]: tmpUrl };
    await api.patch(`/events/${event.event_id}`, upd);
    await api.post(`/events/${event.event_id}/workflow/${step.key}?value=true`);
    toast.success("Saved");
    setEditing(null);
    setTmpUrl("");
    reload();
  };

  const uploadFile = async (step, file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    await api.patch(`/events/${event.event_id}`, { [step.url_field]: r.data.url });
    await api.post(`/events/${event.event_id}/workflow/${step.key}?value=true`);
    toast.success("File uploaded");
    reload();
  };

  const completed = Object.values(ws).filter(Boolean).length;
  const total = STEPS.length;
  const pct = Math.round((completed / total) * 100);

  return (
    <div>
      <div className="dashboard-card p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-lg">Workflow Progress</h3>
          <div className="font-display font-black text-3xl brand-gradient-text">{pct}%</div>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full brand-gradient transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs text-slate-500 mt-2">{completed} of {total} steps completed</div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {STEPS.map((step, i) => {
          const done = !!ws[step.key];
          const url = step.url_field ? event[step.url_field] : null;
          return (
            <div key={step.key} className={`dashboard-card p-5 ${done ? "border-green-200 bg-green-50/30" : ""}`} data-testid={`step-${step.key}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => toggle(step.key, !done)} className={`mt-0.5 w-6 h-6 rounded-lg border-2 flex items-center justify-center ${done ? "bg-green-500 border-green-500" : "border-slate-300"}`} data-testid={`step-toggle-${step.key}`}>
                  {done && <Check className="w-4 h-4 text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                    <div className="font-display font-bold text-slate-900">{step.label}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{step.helper}</div>
                  {step.url_field && (
                    <div className="mt-3">
                      {url && editing !== step.key ? (
                        <div className="flex items-center gap-2 text-xs">
                          <a href={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noreferrer" className="text-orange-600 underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View</a>
                          <button onClick={() => { setEditing(step.key); setTmpUrl(url); }} className="text-slate-500">Replace</button>
                        </div>
                      ) : editing === step.key ? (
                        <div className="flex gap-2">
                          <input value={tmpUrl} onChange={(e) => setTmpUrl(e.target.value)} placeholder="Paste URL" className="input-pill text-xs py-2" />
                          <button onClick={() => saveUrl(step)} className="btn-primary py-2 px-3 text-xs">Save</button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => setEditing(step.key)} className="text-xs px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 font-semibold flex items-center gap-1" data-testid={`step-add-link-${step.key}`}><LinkIcon className="w-3 h-3" /> Add link</button>
                          <label className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 font-semibold flex items-center gap-1 cursor-pointer">
                            <Upload className="w-3 h-3" /> Upload
                            <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(step, e.target.files[0])} />
                          </label>
                          {(step.sample || adminSamples[step.key]) && <a href={adminSamples[step.key] || step.sample} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full bg-violet-50 text-violet-600 font-semibold">Sample</a>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CustomStepsSection event={event} reload={reload} />
    </div>
  );
}

function CustomStepsSection({ event, reload }) {
  const [label, setLabel] = useState("");
  const steps = event.custom_workflow_steps || [];

  const add = async () => {
    if (!label.trim()) return;
    try {
      await api.post(`/events/${event.event_id}/workflow-steps`, { label });
      setLabel("");
      toast.success("Step added");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (s) => {
    await api.patch(`/events/${event.event_id}/workflow-steps/${s.id}`, { done: !s.done });
    reload();
  };

  const del = async (s) => {
    if (!window.confirm(`Remove step "${s.label}"?`)) return;
    await api.delete(`/events/${event.event_id}/workflow-steps/${s.id}`);
    reload();
  };

  return (
    <div className="mt-8 dashboard-card p-5" data-testid="custom-workflow-steps">
      <h3 className="font-display font-black text-lg mb-3 flex items-center gap-2">✨ Custom workflow steps</h3>
      <p className="text-xs text-slate-500 mb-4">Add your own checklist items that aren't in the default workflow. Only PMs / RPMs / Admin can add or remove.</p>
      <div className="flex items-center gap-2 mb-4">
        <input className="input-pill flex-1" placeholder="e.g. Arrange airport pickup for chief guest" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="custom-step-input" />
        <button onClick={add} className="btn-primary" data-testid="custom-step-add"><Check className="w-4 h-4" /> Add</button>
      </div>
      {steps.length === 0 ? (
        <div className="text-slate-400 text-sm">No custom steps yet.</div>
      ) : (
        <div className="space-y-2">
          {steps.map((s) => (
            <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border ${s.done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid={`custom-step-${s.id}`}>
              <button onClick={() => toggle(s)} className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${s.done ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
                {s.done && <Check className="w-4 h-4 text-white" />}
              </button>
              <div className="flex-1">
                <div className={`text-sm font-semibold ${s.done ? "line-through text-slate-400" : ""}`}>{s.label}</div>
                <div className="text-[10px] text-slate-400">Added by {s.created_by_name}{s.done_at && ` · completed ${new Date(s.done_at).toLocaleDateString()}`}</div>
              </div>
              <button onClick={() => del(s)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`custom-step-del-${s.id}`}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
